import { create } from "zustand";
import {
	AuthorizationCodeWithPKCEStrategy,
	SpotifyApi,
	type UserProfile,
} from "@spotify/web-api-ts-sdk";
import { getLocalStorage, setLocalStorage } from "@ds-services/helpers";

type AuthStatus = "checking" | "authenticated" | "loggedout";
type ToastSeverity = "error" | "warning" | "info" | "success";

interface AuthToast {
	message: string;
	severity: ToastSeverity;
}

interface AuthState {
	status: AuthStatus;
	spotify: SpotifyApi | null;
	userProfile: UserProfile | null;
	toast: AuthToast | null;
	checkAuth: () => Promise<void>;
	login: () => Promise<SpotifyApi | null>;
	completeLogin: (spotifyClient: SpotifyApi) => Promise<void>;
	logout: () => void;
	showToast: (message: string, severity?: ToastSeverity) => void;
	clearToast: () => void;
	startExpiryPolling: () => () => void;
}

const spotifyScopes = ["playlist-modify-public"];
const redirectUri = import.meta.env.VITE_REDIRECT_URI ?? "http://127.0.0.1:3000/callback";
const clientId = import.meta.env.VITE_SPOTY_CLIENT_ID ?? "";

const localStorageFields = [
	"access_code",
	"access_token",
	"expir_access_token",
	"profile_api_href",
	"profile_id",
	"profile_image",
	"profile_name",
	"refresh_token",
];

const clearSdkPkceState = () => {
	Object.keys(localStorage)
		.filter((k) => k.startsWith("spotify-sdk:"))
		.forEach((k) => localStorage.removeItem(k));
};

const createSpotifyClient = () => {
	const authStrategy = new AuthorizationCodeWithPKCEStrategy(
		clientId,
		redirectUri,
		spotifyScopes
	);
	return new SpotifyApi(authStrategy);
};

const hasValidStoredToken = () => {
	const accessToken = getLocalStorage("access_token");
	const expiry = getLocalStorage("expir_access_token");
	if (!accessToken || !expiry) return false;
	return Date.now() <= Number(expiry);
};

const persistUserProfile = (profile: UserProfile) => {
	setLocalStorage("profile_image", profile.images[0]?.url ?? "");
	setLocalStorage("profile_name", profile.display_name);
	setLocalStorage("profile_api_href", profile.href);
	setLocalStorage("profile_id", profile.id);
};

export const useAuthStore = create<AuthState>((set, get) => ({
	status: "checking",
	spotify: null,
	userProfile: null,
	toast: null,

	checkAuth: async () => {
		if (get().status === "authenticated" && get().spotify && get().userProfile) return;

		// Only attempt session restore if we have both our stored token AND the SDK's stored token.
		// If either is missing the session is unrecoverable without a fresh login.
		const hasSdkToken = Object.keys(localStorage).some((k) => k.startsWith("spotify-sdk:"));
		if (!hasValidStoredToken() || !hasSdkToken) {
			set({ status: "loggedout", spotify: null, userProfile: null });
			return;
		}

		try {
			const spotify = createSpotifyClient();
			// authenticate() is safe here — SDK has a stored token so it loads it silently, no redirect.
			const authResult = await spotify.authenticate();
			if (!authResult.authenticated) {
				set({ status: "loggedout", spotify: null, userProfile: null });
				return;
			}
			const userProfile = await spotify.currentUser.profile();
			persistUserProfile(userProfile);
			set({ status: "authenticated", spotify, userProfile });
		} catch (error) {
			console.error("Spotify auth check failed:", error);
			set({ status: "loggedout", spotify: null, userProfile: null });
		}
	},

	login: async () => {
		try {
			const spotify = createSpotifyClient();
			await spotify.authenticate();
			return spotify;
		} catch (error) {
			console.error("Spotify login failed:", error);
			get().showToast("Could not start Spotify login. Please try again.", "error");
			return null;
		}
	},

	completeLogin: async (spotifyClient: SpotifyApi) => {
		const token = await spotifyClient.getAccessToken();
		if (token) {
			setLocalStorage("access_token", token.access_token);
			setLocalStorage("refresh_token", token.refresh_token ?? "");
			setLocalStorage(
				"expir_access_token",
				String(token.expires ?? Date.now() + token.expires_in * 1000)
			);
		}

		const userProfile = await spotifyClient.currentUser.profile();
		persistUserProfile(userProfile);
		set({
			status: "authenticated",
			spotify: spotifyClient,
			userProfile,
		});
	},

	logout: () => {
		const { spotify } = get();
		try {
			spotify?.logOut();
		} catch (error) {
			console.error("Spotify logout failed:", error);
		}
		localStorageFields.forEach((field) => localStorage.removeItem(field));
		clearSdkPkceState();
		set({ status: "loggedout", spotify: null, userProfile: null });
	},

	showToast: (message, severity = "info") => {
		set({ toast: { message, severity } });
	},

	clearToast: () => {
		set({ toast: null });
	},

	startExpiryPolling: () => {
		const interval = window.setInterval(() => {
			const state = get();
			if (state.status === "authenticated" && !hasValidStoredToken()) {
				state.showToast("Your Spotify session has expired. Please log in again.", "warning");
				state.logout();
			}
		}, 30_000);

		return () => window.clearInterval(interval);
	},
}));
